import React, { useRef, useEffect, useState, memo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Home } from 'lucide-react';

// Fix for default markers in react-leaflet
if (typeof window !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });
}

// Custom marker icon for properties/rooms
const createPropertyIcon = (color = '#2563eb') => {
  return L.divIcon({
    className: 'custom-property-icon',
    html: `
      <div style="
        background-color: ${color};
        width: 36px;
        height: 36px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 18px;
          margin-top: -4px;
          margin-left: -2px;
        ">🏠</div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

// Pill-style price label marker — shows "from €X" directly on the map without needing to click.
const createPriceLabelIcon = (label, color = '#2563eb', emphasized = false) => {
  const bg = emphasized ? color : 'white';
  const fg = emphasized ? 'white' : color;
  const border = emphasized ? 'white' : color;
  return L.divIcon({
    className: 'custom-price-label-icon',
    html: `
      <div style="
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background-color: ${bg};
        color: ${fg};
        border: 2px solid ${border};
        border-radius: 9999px;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        transform: translateY(-50%);
      ">${label}</div>
    `,
    iconSize: null,
    iconAnchor: [0, 0],
    popupAnchor: [0, -12]
  });
};

// Component to auto-fit bounds when locations change
function MapBoundsFitter({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      const validLocations = locations.filter(loc => loc.latitude && loc.longitude);
      if (validLocations.length === 0) return;

      if (validLocations.length === 1) {
        // Single location: center on it
        const loc = validLocations[0];
        map.setView([parseFloat(loc.latitude), parseFloat(loc.longitude)], 13);
      } else {
        // Multiple locations: fit bounds to show all
        const bounds = validLocations.map(loc => [
          parseFloat(loc.latitude),
          parseFloat(loc.longitude)
        ]);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      }
    }
  }, [locations, map]);

  return null;
}

const LocationMap = memo(function LocationMap({
  locations = [],
  height = "400px",
  className = "",
  defaultCenter = [35.055, 25.423], // Ano Viannos, Crete
  defaultZoom = 11,
  onLocationClick,
  markerColor = '#2563eb'
}) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const mapRef = useRef();
  const containerRef = useRef();

  // Filter locations that have coordinates
  const mappableLocations = locations.filter(
    location => location.latitude && location.longitude
  );

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Handle map load event
  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const handleMarkerClick = (location) => {
    if (onLocationClick) {
      onLocationClick(location);
    }
  };

  // Loading placeholder before locations load
  if (mappableLocations.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`bg-gray-100 rounded-lg p-8 text-center ${className}`}
        style={{ height }}
      >
        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-600 mb-2">No Locations Available</h3>
        <p className="text-gray-500">Location data will appear here once available.</p>
      </div>
    );
  }

  // Show placeholder until map comes into view
  if (!isInView) {
    return (
      <div
        ref={containerRef}
        className={`bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8 text-center ${className}`}
        style={{ height }}
      >
        <MapPin className="w-16 h-16 text-blue-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-700 mb-2">Location Map</h3>
        <p className="text-gray-600 mb-4">
          View {mappableLocations.length} {mappableLocations.length === 1 ? 'location' : 'locations'} on the map
        </p>
        <div className="flex items-center justify-center gap-2 text-blue-600">
          <Home className="w-5 h-5" />
          <span className="font-medium">Property Locations</span>
        </div>
      </div>
    );
  }

  // Error boundary for map rendering
  try {
    return (
      <div className={`relative rounded-lg overflow-hidden shadow-lg ${className}`}>
        {!mapLoaded && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-30">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading Map...</h3>
              <p className="text-gray-500">Preparing location view</p>
            </div>
          </div>
        )}

        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height, width: '100%' }}
          ref={mapRef}
          className="z-10"
          whenReady={handleMapLoad}
          preferCanvas={true}
          zoomControl={true}
          doubleClickZoom={true}
          closePopupOnClick={true}
          dragging={true}
          zoomSnap={1}
          zoomDelta={1}
          trackResize={true}
          touchZoom={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='© Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
            tileSize={256}
            keepBuffer={4}
            updateWhenZooming={false}
            updateWhenIdle={true}
          />

          <MapBoundsFitter locations={mappableLocations} />

          {mappableLocations.map((location, index) => (
            <Marker
              key={location.id || index}
              position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
              icon={location.markerLabel
                ? createPriceLabelIcon(location.markerLabel, markerColor, !!location.emphasized)
                : createPropertyIcon(markerColor)}
              eventHandlers={{
                click: () => handleMarkerClick(location)
              }}
            >
              {/* Tooltip - shows on hover */}
              <Tooltip 
                direction="top" 
                offset={[0, -30]}
                opacity={0.9}
                permanent={false}
              >
                <div className="text-sm font-semibold">
                  {location.tooltipText || location.name || location.property_name || 'Property'}
                </div>
              </Tooltip>

              {/* Popup - shows on click */}
              <Popup maxWidth={300} className="custom-popup">
                <div className="p-2">
                  <h3 className="font-bold text-lg text-gray-900 mb-1">
                    {location.name || location.property_name || 'Property'}
                  </h3>
                  {location.address && (
                    <p className="text-gray-600 text-sm mb-2">{location.address}</p>
                  )}
                  {location.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {location.description}
                    </p>
                  )}
                  {location.google_maps_link && (
                    <a
                      href={location.google_maps_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      <MapPin className="w-4 h-4" />
                      Open in Google Maps
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {mappableLocations.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg px-3 py-2 z-20">
            <p className="text-sm font-medium text-gray-700">
              {mappableLocations.length} Locations
            </p>
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error('Map rendering error:', error);
    return (
      <div
        className={`bg-red-50 border border-red-200 rounded-lg p-8 text-center ${className}`}
        style={{ height }}
      >
        <MapPin className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-700 mb-2">Map Error</h3>
        <p className="text-red-600 mb-4">Unable to load location map</p>
        <p className="text-sm text-red-500">Check console for details</p>
      </div>
    );
  }
});

export default LocationMap;
